// MF-554 — One tile of a Board: asks the backend for its data (scoped to the viewer by the session)
// and draws it with the chart renderer or the item's card component. Loading, empty ("no data" —
// never invented values) and error states are the tile's own, so one failing tile never breaks the
// Board.
import { useEffect, useState } from 'react'
import { Box, Chip, IconButton, Paper, Skeleton, Tooltip, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getBoardTileData, previewLibraryItem } from '../../../../services/boards/boards.service'
import ChartRenderer from './ChartRenderer'
import CARD_COMPONENTS from './cards'
import { SUMMARY_PALETTE, TILE } from './boardTheme'

// request.js rejects with an Error whose message is the backend's description.
// MF-574 — title size is a tile property: small / medium (default) / large
const TITLE_SIZES = { sm: 12, md: 14, lg: 18 }

// An error is shown as text, whatever shape it arrives in (a string, an Error, or an object like { code, message })
const errorText = (err) => {
  if (typeof err === 'string') return err
  const m = err?.message ?? err?.error?.message ?? err?.description
  return typeof m === 'string' && m ? m : 'This tile could not be loaded.'
}

// An empty block: what it is, and WHY it is empty (meta.emptyReason, worked out by the server from the
// school's data) in a calm tone, so "no data" is never read as an error or as something that went missing.
const EmptyState = ({ text, reason }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      px: 2
    }}
  >
    <Box sx={{ maxWidth: 420 }}>
      <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted }}>{text}</Typography>
      {reason ? (
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.faint, mt: 1, lineHeight: 1.45 }}>
          {reason}
        </Typography>
      ) : null}
    </Box>
  </Box>
)

const Centered = ({ children }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      px: 2
    }}
  >
    <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted }}>{children}</Typography>
  </Box>
)

/**
 * @param {{ boardCode: string, tile: Object, studentId?: string, params?: Object, onMeta?: Function,
 *   preview?: boolean, actions?: React.ReactNode, selected?: boolean }} props
 *   preview: Board Builder — data from the Library preview (whole school) instead of the published Board.
 */
const BoardTile = ({
  boardCode,
  tile,
  studentId,
  params,
  onMeta,
  preview = false,
  actions = null,
  selected = false,
  bare = false, // only the drawing, no frame/header (Library thumbnails)
  result // { loading, data, error } from a batch request — when given, the tile does not fetch
}) => {
  const [state, setState] = useState({ loading: true, data: null, error: null })
  const [tableView, setTableView] = useState(false)
  const paramsKey = JSON.stringify(params || {})

  useEffect(() => {
    if (result !== undefined) return undefined
    let alive = true
    setState((s) => ({ ...s, loading: true, error: null }))
    const load = preview
      ? previewLibraryItem(tile.itemCode, tile.overrides?.params || {}, studentId)
      : getBoardTileData(boardCode, tile.id, { params: params || {}, ...(studentId ? { studentId } : {}) })
    load
      .then((data) => {
        if (!alive) return
        setState({ loading: false, data, error: null })
        if (onMeta) onMeta(tile.id, data?.meta)
      })
      .catch((err) => alive && setState({ loading: false, data: null, error: errorText(err) }))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    boardCode,
    tile.id,
    tile.itemCode,
    studentId,
    paramsKey,
    preview,
    result === undefined,
    JSON.stringify(tile.overrides?.params || {})
  ])

  const { loading, data, error } = result !== undefined ? result : state
  const meta = data?.meta || {}
  const title = tile.overrides?.title || meta.title || ''
  // the viewer's ⓘ: the Board's own wording when the builder wrote one, else the Library's
  const description = tile.overrides?.description || meta.description || ''
  const isChart = meta.kind === 'chart'
  const Card = meta.kind === 'card' ? CARD_COMPONENTS[meta.card?.component] : null

  let body
  if (loading) body = <Skeleton variant='rounded' sx={{ height: '100%', borderRadius: '10px' }} />
  else if (error) body = <Centered>{errorText(error)}</Centered>
  else if (meta.empty) body = <EmptyState text={meta.emptyText || 'No data yet.'} reason={meta.emptyReason} />
  else if (isChart) body = <ChartRenderer data={data} overrides={tile.overrides} tableView={tableView} />
  else if (Card) body = <Card data={data} />
  else body = <Centered>This item can’t be shown here yet.</Centered>

  if (bare) return <Box sx={{ height: '100%', minHeight: 0, pointerEvents: 'none' }}>{body}</Box>

  return (
    <Paper
      variant='outlined'
      sx={{
        borderColor: selected ? 'primary.main' : TILE.border,
        borderWidth: selected ? 2 : 1,
        borderRadius: TILE.radius,
        p: TILE.pad,
        // border-box: padding and border stay INSIDE the grid cell (else the tile is ~34px taller
        // than its cell and spills into the next row).
        boxSizing: 'border-box',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 24, mb: 1 }}>
        <Typography
          noWrap
          sx={{
            fontSize: TITLE_SIZES[tile.overrides?.titleStyle?.size] || TITLE_SIZES.md,
            fontWeight: tile.overrides?.titleStyle?.bold === false ? 400 : 600,
            fontStyle: tile.overrides?.titleStyle?.italic ? 'italic' : 'normal',
            color: SUMMARY_PALETTE.ink,
            flex: 1,
            minWidth: 48
          }}
          title={title}
        >
          {title || (loading ? '' : '—')}
        </Typography>
        {meta.sample ? (
          <Tooltip
            title='Made-up numbers to show how this looks — the real data replaces them'
            placement='top'
          >
            <Chip size='small' label='Sample' sx={{ height: 18, fontSize: 10, mr: 0.5, flexShrink: 0 }} />
          </Tooltip>
        ) : null}
        {description && tile.overrides?.descriptionPlacement !== 'below' ? (
          <Tooltip title={description} placement='top'>
            <Box
              component='span'
              sx={{ color: SUMMARY_PALETTE.faint, fontSize: 13, cursor: 'help', px: 0.5 }}
            >
              <FontAwesomeIcon icon={['fal', 'info-circle']} />
            </Box>
          </Tooltip>
        ) : null}
        {isChart && !meta.empty ? (
          <Tooltip title={tableView ? 'Show chart' : 'Show table'}>
            <IconButton
              size='small'
              onClick={() => setTableView((v) => !v)}
              aria-label={tableView ? 'Show chart' : 'Show table'}
            >
              <FontAwesomeIcon icon={['fal', tableView ? 'chart-bar' : 'table']} style={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        ) : null}
        {actions}
      </Box>
      {description && tile.overrides?.descriptionPlacement === 'below' ? (
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mb: 1, lineHeight: 1.4 }}>
          {description}
        </Typography>
      ) : null}
      {/* Breathing room under the plot / big number so it never sits on the tile's bottom edge. */}
      <Box sx={{ flex: 1, minHeight: 0, pb: 0.5 }}>{body}</Box>
    </Paper>
  )
}

export default BoardTile
